import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Archive, FileText, Plus, RotateCcw, Search, Settings } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import JaceLayout from "./JaceLayout";
import { JACE, STATUS_COLORS, fmtDateNL } from "./shared";

export default function JaceFeedback() {
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [archived, setArchived] = useState(false);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [sessionPicker, setSessionPicker] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [pickerAthletes, setPickerAthletes] = useState([]);

  const load = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (status) params.set("status", status);
    if (archived) params.set("archived", "true");
    api.get(`/jace/feedback?${params}`).then(({ data }) => setItems(data.items || [])).catch(() => setItems([]));
  };
  useEffect(load, [archived, status]); // eslint-disable-line react-hooks/exhaustive-deps

  const openPicker = async () => {
    try {
      const [{ data: sdata }, { data: adata }] = await Promise.all([api.get("/jace/sessions"), api.get("/jace/athletes")]);
      setSessions((sdata.items || []).filter((s) => ["COMPLETED", "REVIEWED"].includes(s.status)));
      setPickerAthletes(adata.items || []);
      setSessionPicker(true);
    } catch { toast.error("Gegevens laden mislukt"); }
  };

  const toggleArchive = async (item, value) => {
    try {
      await api.post(`/jace/feedback/${item.id}/archive`, { archived: value });
      toast.success(value ? "Feedback gearchiveerd" : "Feedback hersteld");
      load();
    } catch { toast.error("Archiveren mislukt"); }
  };

  const athletes = useMemo(() => [...new Set((items || []).map((i) => i.athlete_name).filter(Boolean))], [items]);

  return (
    <JaceLayout>
      <div data-testid="jace-feedback-page">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="section-tag section-tag-dark mb-3">// FEEDBACK — ATHLETE REPORTS</div>
            <h2 className="font-display uppercase text-4xl md:text-6xl">Feedback.</h2>
            <p className="text-sm text-[#A3A3A3] mt-2 max-w-2xl">Privé coach notes, goedgekeurde atletrapporten en chronologische ontwikkeling per atleet.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/jace/feedback/settings" className="btn-ghost-dark inline-flex items-center gap-2" data-testid="jace-feedback-settings-btn"><Settings size={13}/> SETTINGS</Link>
            <button onClick={openPicker} className="chip px-4 py-2.5 inline-flex items-center gap-2" style={{ background: JACE, color: "#0A0A0A" }} data-testid="jace-feedback-new-btn"><Plus size={13}/> NEW FEEDBACK</button>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mb-6">
          <div className="relative flex-1 min-w-56">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A4A4A]"/>
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Zoek atleet of sessie…" className="editorial-input dark w-full !pl-9" data-testid="jace-feedback-search"/>
          </div>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="editorial-input dark !w-auto" data-testid="jace-feedback-status-filter">
            <option value="" className="bg-[#0A0A0A]">ALLE STATUS</option>
            <option value="draft" className="bg-[#0A0A0A]">CONCEPT</option>
            <option value="final" className="bg-[#0A0A0A]">DEFINITIEF</option>
          </select>
          <button onClick={() => setArchived(!archived)} className="chip px-3 py-2 border transition-colors" style={archived ? { borderColor: JACE, color: JACE } : { borderColor: "#F5F3F033", color: "#A3A3A3" }} data-testid="jace-feedback-archived-toggle">
            {archived ? "ARCHIVE ACTIEF" : "TOON ARCHIVE"}
          </button>
          <button onClick={load} className="btn-ghost-dark" data-testid="jace-feedback-apply">ZOEK</button>
        </div>

        {items === null ? <div className="chip text-[#A3A3A3]">Laden…</div> : items.length === 0 ? (
          <div className="border border-[#F5F3F0]/15 p-10 text-center" data-testid="jace-feedback-empty">
            <FileText size={26} className="mx-auto" style={{ color: JACE }}/>
            <div className="font-display uppercase text-2xl mt-4">Nog geen feedback.</div>
            <div className="chip text-[#4A4A4A] mt-2">Start vanuit een afgeronde sessie of voeg later handmatige feedback toe.</div>
          </div>
        ) : (
          <div className="line-t line-dark" data-testid="jace-feedback-list">
            {items.map((item) => (
              <div key={item.id} className="line-b line-dark py-4 flex flex-wrap items-center gap-4" data-testid={`jace-feedback-row-${item.id}`}>
                <button onClick={() => nav(`/jace/feedback/${item.id}`)} className="flex-1 min-w-56 text-left">
                  <div className="font-display uppercase text-xl">{item.athlete_name}</div>
                  <div className="chip text-[#A3A3A3] mt-1">{item.session_title || "Handmatige evaluatie"} · {item.training_date ? fmtDateNL(item.training_date) : "geen datum"}</div>
                </button>
                <span className="chip px-2 py-1 border" style={{ color: STATUS_COLORS[item.status === "final" ? "COMPLETED" : "DRAFT"], borderColor: "currentColor" }} data-testid={`jace-feedback-status-${item.id}`}>{item.status === "final" ? "DEFINITIEF" : "CONCEPT"}</span>
                <span className="chip px-2 py-1 border" style={item.report?.status === "final" ? { color: "#4ADE80", borderColor: "#4ADE8055" } : { color: "#A3A3A3", borderColor: "#F5F3F033" }} data-testid={`jace-report-status-${item.id}`}>
                  {item.report?.status === "final" ? "RAPPORT KLAAR" : item.report ? "RAPPORT CONCEPT" : "GEEN RAPPORT"}
                </span>
                <button onClick={() => toggleArchive(item, !archived)} className="chip px-3 py-2 border border-[#F5F3F0]/25 text-[#A3A3A3] hover:text-white inline-flex items-center gap-1.5" data-testid={archived ? `jace-feedback-restore-${item.id}` : `jace-feedback-archive-${item.id}`}>
                  {archived ? <><RotateCcw size={12}/> HERSTEL</> : <><Archive size={12}/> ARCHIVE</>}
                </button>
              </div>
            ))}
          </div>
        )}
        {athletes.length > 0 && <div className="chip text-[#4A4A4A] mt-6" data-testid="jace-feedback-athlete-count">{athletes.length} atleet(en) in deze feedbackhistorie</div>}

        {sessionPicker && (
          <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSessionPicker(false)}>
            <div className="bg-[#0A0A0A] border border-[#F5F3F0]/20 max-w-lg w-full p-6 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="jace-feedback-session-picker">
              <div className="chip mb-2" style={{ color: JACE }}>// KIES AFGERONDE SESSIE</div>
              <div className="space-y-2 mt-4">
                {sessions.length === 0 && <div className="chip text-[#4A4A4A] py-6">Geen afgeronde sessies gevonden.</div>}
                {sessions.map((s) => (
                  <button key={s.session_id} onClick={() => nav(`/jace/feedback/new?session=${s.session_id}`)} className="w-full text-left border border-[#F5F3F0]/15 p-4 hover:border-[#C6FF3C] transition-colors" data-testid={`jace-feedback-pick-${s.session_id}`}>
                    <div className="font-display uppercase text-lg">{s.athlete || s.title}</div>
                    <div className="chip text-[#A3A3A3] mt-1">{s.session_id} · {s.date} · {s.status}</div>
                  </button>
                ))}
              </div>
              <div className="chip mt-8 mb-2" style={{ color: JACE }}>// OF KIES ATLEET (ZONDER SESSIE)</div>
              <div className="space-y-2 mt-4">
                {pickerAthletes.length === 0 && <div className="chip text-[#4A4A4A] py-6">Nog geen atleten — maak ze aan onder ATHLETES.</div>}
                {pickerAthletes.map((a) => (
                  <button key={a.id} onClick={() => nav(`/jace/feedback/new?athlete=${a.id}`)} className="w-full text-left border border-[#F5F3F0]/15 p-4 hover:border-[#C6FF3C] transition-colors" data-testid={`jace-feedback-pick-athlete-${a.id}`}>
                    <div className="font-display uppercase text-lg">{a.name}</div>
                    <div className="chip text-[#A3A3A3] mt-1">Losse evaluatie{a.email ? ` · ${a.email}` : ""}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </JaceLayout>
  );
}
